import { useCallback, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Upload, X } from "lucide-react";

type Props = {
  /** Folder path within the cms bucket (e.g. "videos", "blog-images") */
  folder: string;
  /** Accepted file types (e.g. "video/*", "image/*") */
  accept: string;
  /** Called with the public URL after successful upload */
  onUploaded: (url: string) => void;
  /** Label shown on the button */
  label?: string;
  /** Prevent opening the picker while an external save is in flight. */
  disabled?: boolean;
};

function getContentTypeAttempts(file: File, accept: string) {
  const isAudioUpload =
    accept.includes("audio/") ||
    file.type.startsWith("audio/") ||
    file.name.toLowerCase().endsWith(".mp3");

  if (!isAudioUpload) return [undefined];

  return Array.from(new Set([file.type, "audio/mpeg", "audio/mp3"].filter(Boolean)));
}

function isUnsupportedMimeTypeError(error: { message?: string }) {
  const message = error.message?.toLowerCase() ?? "";
  return message.includes("mime type") && message.includes("not supported");
}

export function FileUpload({ folder, accept, onUploaded, label = "Upload file", disabled = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setUploading(true);
      setProgress("Uploading…");

      const ext = file.name.split(".").pop() ?? "";
      const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const path = `${folder}/${safeName}`;

      let uploadError: { message?: string } | null = null;

      for (const contentType of getContentTypeAttempts(file, accept)) {
        const { error } = await supabase.storage
          .from("cms")
          .upload(path, file, {
            cacheControl: "31536000",
            upsert: false,
            ...(contentType ? { contentType } : {}),
          });

        if (!error) {
          uploadError = null;
          break;
        }

        uploadError = error;
        if (!isUnsupportedMimeTypeError(error)) break;
      }

      if (uploadError) {
        setError(uploadError.message ?? "Upload failed");
        setUploading(false);
        setProgress(null);
        return;
      }

      const { data: urlData } = supabase.storage.from("cms").getPublicUrl(path);
      setUploading(false);
      setProgress(null);
      onUploaded(urlData.publicUrl);
    },
    [accept, folder, onUploaded],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  return (
    <div className="inline-flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || disabled}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
      >
        <Upload className="h-3.5 w-3.5" />
        {progress ?? label}
      </button>
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-600">
          <X className="h-3 w-3" />
          {error}
        </div>
      )}
    </div>
  );
}
